import React from "react"
import { twMerge } from "tailwind-merge"

import { DEFAULT_HIGHLIGHT_CLASSES } from "../constants"
import { TextLogoView } from "../sitewide-styles/components/TextLogoView"
import { renderBuilderV2SocialIcon } from "../utils/builderV2SocialIcons"
import { formatHeadingFontFamily } from "../utils/fontUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import {
  pageVisibleInBuilderNav,
  publicPathForBuilderPage,
  scopePath,
  buildBookingUrl,
  buildServiceUrl
} from "../utils/urlUtils"
import { useOptionalWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"

import { ScrollRevealItem } from "./shared/ScrollReveal"

const FooterSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  // Context is null on public page (no Provider); use practice pages in that case
  const context = useOptionalWebsiteBuilderV2()
  const contextPages = Array.isArray(context?.pages) ? context.pages : []
  const textLogoBuilderEnabled = context?.textLogoBuilderEnabled ?? false
  const practicePages = Array.isArray(practice?.pages) ? practice.pages : []
  const availablePages = contextPages.length > 0 ? contextPages : practicePages

  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const showLogo = settings.showLogo || true
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || theme?.footer?.buttonText || "Book a Free Discovery Call"
  const ctaAction = theme?.footer?.ctaAction || settings.ctaAction || "booking"
  const ctaTarget = theme?.footer?.ctaTarget || settings.ctaTarget || ""
  const showSocialLinks = settings.showSocialLinks || true
  const showPoweredBy = settings.showPoweredBy || true

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          const targetUrl = scopePath(`/${ctaTarget}`, practice)
          window.location.href = targetUrl
        }
        break
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const socialLinks = [
    { name: "Instagram", icon: "instagram", url: theme?.footer?.socialLinks?.instagram },
    { name: "TikTok", icon: "tiktok", url: theme?.footer?.socialLinks?.tiktok },
    { name: "X", icon: "x", url: theme?.footer?.socialLinks?.x },
    { name: "Facebook", icon: "facebook", url: theme?.footer?.socialLinks?.facebook },
    { name: "YouTube", icon: "youtube", url: theme?.footer?.socialLinks?.youtube },
    { name: "LinkedIn", icon: "linkedin", url: theme?.footer?.socialLinks?.linkedin },
    { name: "Substack", icon: "substack", url: theme?.footer?.socialLinks?.substack },
    { name: "Pinterest", icon: "pinterest", url: theme?.footer?.socialLinks?.pinterest }
  ]

  return (
    <section
      className={twMerge(
        "relative z-10 flex min-h-[200px] w-full items-center justify-center py-16",
        isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
      )}
      style={{ backgroundColor: theme?.colors?.bgDark }}>
      <div className="mx-auto w-full max-w-[1160px] px-1 lg:px-24 md:px-16">
        <div className="flex flex-col gap-12">
          <ScrollRevealItem staggerIndex={0} className="flex w-full items-start justify-between gap-12">
            {/* Left - Logo */}
            <div className="flex flex-1 items-start">
              {showLogo &&
                (textLogoBuilderEnabled && theme?.footer?.logoType === "text" && theme?.footer?.textLogo ? (
                  <a
                    href={scopePath("/", practice)}
                    className="flex shrink-0 flex-col items-center justify-center overflow-hidden transition-opacity hover:opacity-80">
                    {(() => {
                      const tl = theme?.footer?.textLogo
                      const logoSize = theme?.footer?.logoSize ?? 72
                      // Honor user-picked font sizes verbatim. Only the fallback (legacy
                      // themes without explicit sizes) is derived from logoSize.
                      const mainFontSize = tl?.mainFontSize ?? Math.round(logoSize * 0.35)
                      const secondFontSize = tl?.secondFontSize
                      return (
                        <TextLogoView
                          mainLine={tl?.mainLine || practice?.name || "Practice Name"}
                          secondLine={tl?.secondLine}
                          mainFont={tl?.mainFont || "Inter"}
                          secondFont={tl?.secondFont || "Inter"}
                          color={theme?.colors?.textLight || "#FFFFFF"}
                          mainFontSize={`${mainFontSize}px`}
                          secondFontSize={secondFontSize != null ? `${secondFontSize}px` : undefined}
                          mainLetterSpacing={tl?.mainLetterSpacing}
                          secondLetterSpacing={tl?.secondLetterSpacing}
                          lineSpacing={tl?.lineSpacing}
                          alignment={tl?.alignment}
                        />
                      )
                    })()}
                  </a>
                ) : theme?.footer?.logo || theme?.footer?.whiteLogo ? (
                  <a
                    href={scopePath("/", practice)}
                    className="flex shrink-0 flex-col items-center justify-center overflow-hidden transition-opacity hover:opacity-80">
                    <img
                      src={theme?.footer?.whiteLogo || theme?.footer?.logo}
                      alt={practice?.name || "Practice Logo"}
                      className="object-contain"
                      style={{
                        width: `${theme?.footer?.logoSize || 72}px`,
                        height: "auto",
                        borderRadius: theme?.borderRadius || "0px"
                      }}
                    />
                  </a>
                ) : (
                  <a
                    href={scopePath("/", practice)}
                    className="flex shrink-0 flex-col items-center justify-center overflow-hidden transition-opacity hover:opacity-80">
                    <span
                      className="text-lg font-bold"
                      style={{
                        fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                        color: theme?.colors?.textLight,
                        fontSize: `${(theme?.footer?.logoSize || 141) / 8}px`
                      }}>
                      {practice?.name || "Practice Name"}
                    </span>
                  </a>
                ))}
            </div>

            {/* Right - CTA Button */}
            <div className="flex items-start">
              {showCTA && (
                <button
                  onClick={handleCTAClick}
                  className={twMerge(
                    "flex items-center justify-center gap-3 px-10 py-3 transition-all hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground || "#FFF",
                      color: theme?.footer?.ctaButtonText || theme?.colors?.textDark || "#000",
                      fontFamily: theme?.fonts?.paragraph,
                      fontWeight: "400",
                      fontSize: "16px",
                      lineHeight: "24px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor =
                      theme?.colors?.primaryDark ||
                      theme?.footer?.ctaButtonBackground ||
                      theme?.colors?.buttonBackground
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor =
                      theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground
                  }}>
                  <span className="text-base font-normal leading-6">{ctaText}</span>
                </button>
              )}
            </div>
          </ScrollRevealItem>

          <ScrollRevealItem staggerIndex={1} className="flex w-full items-start justify-between gap-12">
            {/* Left - Navigation Links */}
            <div className="flex flex-1 flex-wrap items-start justify-start gap-x-8 gap-y-4">
              {/* Home link - only show if enabled */}
              {theme?.footer?.showHome && (
                <a
                  href={scopePath("/", practice)}
                  className="text-base font-normal leading-6 transition-opacity hover:opacity-80"
                  style={{
                    fontFamily: theme?.fonts?.paragraph,
                    color: theme?.colors?.textLight
                  }}>
                  Home
                </a>
              )}
              {/* Other pages - include default pages (except home) and custom pages */}
              {availablePages
                .filter((page) => page.defaultPageType !== "home" && pageVisibleInBuilderNav(page, practice))
                .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order (similar to sections)
                .map((page) => (
                  <a
                    key={page.id}
                    href={publicPathForBuilderPage(page, practice)}
                    className="text-base font-normal leading-6 transition-opacity hover:opacity-80"
                    style={{
                      fontFamily: theme?.fonts?.paragraph,
                      color: theme?.colors?.textLight
                    }}>
                    {page.title}
                  </a>
                ))}
            </div>

            {/* Right - Social Links */}
            {showSocialLinks && (
              <div className="flex items-center justify-end gap-8">
                {socialLinks.map(
                  (social) =>
                    social.url && (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-6 items-center justify-center transition-opacity hover:opacity-80"
                        style={{ color: theme?.colors?.textLight }}>
                        {renderBuilderV2SocialIcon(social.icon)}
                      </a>
                    )
                )}
              </div>
            )}
          </ScrollRevealItem>

          <div className="h-px w-full" style={{ backgroundColor: theme?.colors?.textLight, opacity: 0.2 }} />

          <ScrollRevealItem staggerIndex={2} className="flex w-full flex-col items-center gap-8">
            {/* Disclaimer Text */}
            {theme?.footer?.disclaimerText && (
              <p
                className="text-center text-base leading-6"
                style={{
                  fontFamily: theme?.fonts?.paragraph,
                  color: theme?.colors?.textLight
                }}>
                {theme.footer.disclaimerText}
              </p>
            )}

            {/* Powered by */}
            {showPoweredBy && (
              <a
                href="https://heal.me"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-0 transition-opacity hover:opacity-80">
                <div className="flex size-8 items-center justify-center overflow-hidden rounded">
                  <img
                    src={require("../../../../assets/images/HealmeLogo.png")}
                    alt="Heal.me"
                    className="size-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-center rounded-[26px] px-3 py-1">
                  <span
                    className="text-xs font-semibold uppercase tracking-[0.75px]"
                    style={{
                      fontFamily: theme?.fonts?.paragraph,
                      color: theme?.colors?.textLight
                    }}>
                    Powered by Heal.me
                  </span>
                </div>
              </a>
            )}
          </ScrollRevealItem>
        </div>
      </div>
    </section>
  )
}

export default FooterSectionDesktop
